import { asyncHandler } from '../async-handler';
import store from '../../../store/store';
import { RequestInfo } from '../../../interfaces/RequestInfo';
import {
  EXTERNAL_API_TYPES_API,
  EXTERNAL_API_KEYS_API,
  EXTERNAL_API_KEY_UPDATE_API,
  EXTERNAL_API_KEY_DELETE_API,
} from '../../constants/central-api';
import { applicationWWWXRLEncodedBodyBuilder } from '../../utils/body-builder';

export interface ExternalApiType {
  slug: string;
  configs: {
    fields: {
      [key: string]: {
        type: string;
        required: boolean;
      };
    };
  };
}

export interface ExternalApiTypesResponse {
  success: boolean;
  message: string;
  data: ExternalApiType[];
}

export interface ExternalApiKey {
  id: number;
  name: string;
  type: string;
  /** Only sent by older API versions; current ones never return the key. */
  api_key?: string | null;
  api_secret?: string | null;
  /** Whether a key is stored for this integration. */
  has_api_key?: boolean;
  /** Last four characters of the stored key, for display only. */
  api_key_hint?: string | null;
  configs: any[];
  accepted_hosts: any;
  additional_configs: any;
  is_active: number;
  integrable_type: string;
  integrable_id: string;
  created_at: string;
  updated_at: string;
  slug: any;
  deleted_at: any;
  category: string;
  is_encrypted: number;
  user_id: string;
  read_scope: number;
  write_scope: number;
}

/** Dots shown in front of the stored key's hint. */
export const EXTERNAL_API_KEY_MASK_DOTS = '••••••••';

/**
 * The masked form of a stored key ("••••••••abcd"), or null when nothing is
 * stored. Older responses that still carry the full key are reduced to the
 * same hint so the key itself never reaches the page.
 */
export function getExternalApiKeyMask(key?: ExternalApiKey | null) {
  if (!key) return null;
  let hint = key.api_key_hint ?? null;
  if (!hint && typeof key.api_key === 'string' && key.api_key.length > 0) {
    hint = key.api_key.slice(-4);
  }
  const stored = key.has_api_key ?? !!hint;
  if (!stored) return null;
  return EXTERNAL_API_KEY_MASK_DOTS + (hint ?? '');
}

/**
 * The key to send on update: the trimmed replacement the user typed, or null
 * when the input is empty or unchanged from the mask (nothing to update).
 */
export function getExternalApiKeyReplacement(
  typed: string | undefined,
  mask: string | null
) {
  const value = (typed ?? '').trim();
  if (value === '' || value === mask) return null;
  if (value.startsWith(EXTERNAL_API_KEY_MASK_DOTS.slice(0, 4))) return null;
  return value;
}

export interface ExternalApiKeysResponse {
  success: boolean;
  message: string;
  data: ExternalApiKey[];
}

export interface CreateExternalApiKeyRequest {
  type: string;
  api_key: string;
}

export interface CreateExternalApiKeyResponse {
  success: boolean;
  message: string;
  data: ExternalApiKey;
}

export interface UpdateExternalApiKeyRequest {
  api_key: string;
}

export interface UpdateExternalApiKeyResponse {
  success: boolean;
  message: string;
  data: ExternalApiKey;
}

export interface DeleteExternalApiKeyResponse {
  success: boolean;
  message: string;
  data: null;
}

export default async function get_external_api_types_api_call(): Promise<
  ExternalApiType[] | null
> {
  const accessToken = store.getState().auth.authorization.accessToken;
  const sellerToken = accessToken?.sellerToken;

  const requestInfo: RequestInfo = {
    url: EXTERNAL_API_TYPES_API,
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: accessToken.type + ' ' + sellerToken,
    },
  };

  const actionType = 'GET_EXTERNAL_API_TYPES';
  const customMsg = 'External API Types Fetched Successfully.';

  const res: any = await asyncHandler(
    requestInfo,
    actionType,
    customMsg,
    false
  );

  const response: ExternalApiTypesResponse = res?.data;
  if (!response?.success) {
    return null;
  }

  if (res.actionType === actionType && response.success === true) {
    return response.data || [];
  }

  return null;
}

export async function get_external_api_keys_api_call(): Promise<
  ExternalApiKey[] | null
> {
  const shopId = store.getState().user?.company?.shop_id;
  const accessToken = store.getState().auth.authorization.accessToken;
  const sellerToken = accessToken?.sellerToken;

  const requestInfo: RequestInfo = {
    url: EXTERNAL_API_KEYS_API(shopId),
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: accessToken.type + ' ' + sellerToken,
    },
  };

  const actionType = 'GET_EXTERNAL_API_KEYS';
  const customMsg = 'External API Keys Fetched Successfully.';

  const res: any = await asyncHandler(
    requestInfo,
    actionType,
    customMsg,
    false
  );

  const response: ExternalApiKeysResponse = res?.data;
  if (!response?.success) {
    return null;
  }

  if (res.actionType === actionType && response.success === true) {
    return response.data || [];
  }

  return null;
}

export async function create_external_api_key_api_call(
  keyData: CreateExternalApiKeyRequest
): Promise<boolean> {
  const shopId = store.getState().user?.company?.shop_id;
  const accessToken = store.getState().auth.authorization.accessToken;
  const sellerToken = accessToken?.sellerToken;

  const requestInfo: RequestInfo = {
    url: EXTERNAL_API_KEYS_API(shopId),
    method: 'POST',
    body: applicationWWWXRLEncodedBodyBuilder(keyData),
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: accessToken.type + ' ' + sellerToken,
    },
  };

  const actionType = 'CREATE_EXTERNAL_API_KEY';
  const customMsg = 'External API Key Created Successfully.';

  const res: any = await asyncHandler(
    requestInfo,
    actionType,
    customMsg,
    true,
    false
  );

  const response: CreateExternalApiKeyResponse = res?.data;
  if (!response?.success) {
    return false;
  }

  if (res.actionType === actionType && response.success === true) {
    return true;
  }

  return false;
}

export async function update_external_api_key_api_call(
  apiKeyId: string,
  keyData: UpdateExternalApiKeyRequest
): Promise<boolean> {
  const accessToken = store.getState().auth.authorization.accessToken;
  const sellerToken = accessToken?.sellerToken;

  const requestInfo: RequestInfo = {
    url: EXTERNAL_API_KEY_UPDATE_API(apiKeyId),
    method: 'PATCH',
    body: applicationWWWXRLEncodedBodyBuilder(keyData),
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: accessToken.type + ' ' + sellerToken,
    },
  };

  const actionType = 'UPDATE_EXTERNAL_API_KEY';
  const customMsg = 'External API Key Updated Successfully.';

  const res: any = await asyncHandler(
    requestInfo,
    actionType,
    customMsg,
    true,
    false
  );

  const response: UpdateExternalApiKeyResponse = res?.data;
  if (!response?.success) {
    return false;
  }

  if (res.actionType === actionType && response.success === true) {
    return true;
  }

  return false;
}

export async function delete_external_api_key_api_call(
  apiKeyId: string
): Promise<boolean> {
  const accessToken = store.getState().auth.authorization.accessToken;
  const sellerToken = accessToken?.sellerToken;

  const requestInfo: RequestInfo = {
    url: EXTERNAL_API_KEY_DELETE_API(apiKeyId),
    method: 'DELETE',
    headers: {
      Accept: 'application/json',
      Authorization: accessToken.type + ' ' + sellerToken,
    },
  };

  const actionType = 'DELETE_EXTERNAL_API_KEY';
  const customMsg = 'External API Key Deleted Successfully.';

  const res: any = await asyncHandler(
    requestInfo,
    actionType,
    customMsg,
    true,
    false
  );

  const response: DeleteExternalApiKeyResponse = res?.data;
  if (!response?.success) {
    return false;
  }

  if (res.actionType === actionType && response.success === true) {
    return true;
  }

  return false;
}
